// src/services/auth.js
import { authAPI, API_BASE } from './api';
import { canonicalizeRole } from '../utils/role';

// Keys for localStorage
const TOKEN_KEY = 'token';
const USER_KEY = 'user';

const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE).origin;
  } catch (error) {
    console.error('Failed to parse API base URL', error);
    return window.location.origin;
  }
})();

const normalizeOrigin = (value) => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const buildOriginVariants = (origin) => {
  const normalized = normalizeOrigin(origin);
  if (!normalized) {
    return [];
  }

  const variants = new Set([normalized]);

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname || '';
    const isLocalHost = host === 'localhost';
    const isIpv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
    const canToggleWww = host.includes('.') && !isLocalHost && !isIpv4;

    if (host.startsWith('www.')) {
      parsed.hostname = host.slice(4);
      variants.add(parsed.origin);
    } else if (canToggleWww) {
      parsed.hostname = `www.${host}`;
      variants.add(parsed.origin);
    }
  } catch {
    // Ignore malformed origins and keep the normalized value.
  }

  return Array.from(variants);
};

const resolveOAuthAllowedOrigins = () => {
  const allowed = new Set();
  const configuredOrigins = (process.env.REACT_APP_OAUTH_ALLOWED_ORIGINS || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  [API_ORIGIN, window.location.origin, ...configuredOrigins].forEach((origin) => {
    buildOriginVariants(origin).forEach((variant) => allowed.add(variant));
  });

  return allowed;
};

const OAUTH_ALLOWED_ORIGINS = resolveOAuthAllowedOrigins();

const isExpectedOAuthMessageOrigin = (origin) => {
  const normalized = normalizeOrigin(origin);
  return Boolean(normalized && OAUTH_ALLOWED_ORIGINS.has(normalized));
};

const persistUser = (user) => {
  if (!user) {
    localStorage.removeItem(USER_KEY);
    return null;
  }

  const canonicalRole = canonicalizeRole(user.role || user.legacyRole, user.accountType);
  const legacyRole = user.legacyRole || user.role || 'student';

  const normalizedUser = {
    ...user,
    role: canonicalRole || legacyRole,
    legacyRole,
    accountType: user.accountType || 'individual',
    universityId: user.universityId || null,
    permissions: {
      ...user.permissions,
      isGlobalAdmin: user.permissions?.isGlobalAdmin ?? canonicalRole === 'global_admin',
      isUniversityAdmin: user.permissions?.isUniversityAdmin ?? canonicalRole === 'university_admin',
    },
  };

  localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
  return normalizedUser;
};

export const persistAuthSession = ({ token, user }) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  return persistUser(user);
};

export const updateStoredUser = (partial = {}) => {
  const current = getCurrentUser();
  if (!current) {
    return null;
  }

  return persistUser({
    ...current,
    ...partial,
  });
};

const redeemSessionCode = async (sessionCode) => {
  const trimmed = sessionCode?.trim?.();
  if (!trimmed) {
    throw new Error('A valid session code is required to complete sign-in.');
  }

  const response = await authAPI.exchangeSessionCode(trimmed);
  return response.data;
};

export const exchangeSessionCode = async (sessionCode) => {
  try {
    const payload = await redeemSessionCode(sessionCode);
    const normalizedUser = persistAuthSession(payload);
    return {
      ...payload,
      user: normalizedUser
    };
  } catch (error) {
    const message = error?.message || error.response?.data?.message;
    throw new Error(message || 'Unable to finalise your session code exchange.');
  }
};

const resolveOAuthSession = async (payload) => {
  if (!payload) {
    throw new Error('Unable to complete sign-in. Missing OAuth session data.');
  }

  if (payload.token) {
    const normalizedUser = persistAuthSession(payload);
    return {
      ...payload,
      user: normalizedUser
    };
  }

  if (payload.sessionCode) {
    return exchangeSessionCode(payload.sessionCode);
  }

  throw new Error('OAuth provider response did not include a session token.');
};

/**
 * Register an individual self-serve account
 */
export const registerIndividual = async (userData) => {
  const payload = {
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    password: userData.password,
    role: userData.role,
    planPreference: userData.planPreference,
    marketingOptIn: Boolean(userData.marketingOptIn),
  };

  if (userData.institution) {
    payload.institution = typeof userData.institution === 'string'
      ? userData.institution.trim()
      : userData.institution;
  }

  if (Array.isArray(userData.licenseIds)) {
    payload.licenseIds = userData.licenseIds;
  }

  if (userData.licenseId) {
    payload.licenseId = userData.licenseId;
  }

  const response = await authAPI.registerIndividual(payload);
  return response.data;
};

export const registerWithInvite = async (userData) => {
  const payload = {
    inviteToken: userData.inviteToken,
    email: userData.email,
    password: userData.password,
    firstName: userData.firstName,
    lastName: userData.lastName,
  };

  const response = await authAPI.registerWithInvite(payload);
  return response.data;
};

export const registerWithDomain = async (userData) => {
  const payload = {
    email: userData.email,
    password: userData.password,
    firstName: userData.firstName,
    lastName: userData.lastName,
    universityId: userData.universityId,
    universitySlug: userData.universitySlug,
    marketingOptIn: Boolean(userData.marketingOptIn),
  };

  const response = await authAPI.registerWithDomain(payload);
  return response.data;
};

export const previewInvite = async (token) => {
  const response = await authAPI.previewInvite(token);
  return response.data;
};

export const fetchSignupExperienceConfig = async () => {
  try {
    const response = await authAPI.getSignupExperienceConfig();
    const payload = response.data || {};

    const normalizeInteger = (value, fallback) => {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
    };

    return {
      freeSignupTokens: normalizeInteger(payload.freeSignupTokens, 0),
      historyCostTokens: normalizeInteger(payload.historyCostTokens, 1),
      updatedAt: payload.updatedAt || null,
    };
  } catch (error) {
    console.error('Failed to load signup configuration', error);
    throw error;
  }
};

// Backwards compatibility for existing callers
export const register = registerIndividual;

/**
 * Log in a user
 */
export const login = async (credentials) => {
  const response = await authAPI.login(credentials);
  const normalizedUser = persistAuthSession(response.data);
  return {
    ...response.data,
    user: normalizedUser,
  };
};

const buildOAuthLoginUrl = (provider, options = {}) => {
  const url = new URL(`/api/auth/oauth/${provider}`, API_BASE);
  url.searchParams.set('origin', window.location.origin);
  url.searchParams.set('redirect', options.redirect || window.location.origin);
  if (options.prompt) {
    url.searchParams.set('prompt', options.prompt);
  }
  return url.toString();
};

const POPUP_TIMEOUT_MS = 2 * 60 * 1000;

const openOAuthPopup = (provider, url) => new Promise((resolve, reject) => {
  const popup = window.open(url, `vm-${provider}-oauth`, 'width=600,height=720,menubar=no,toolbar=no,status=no');
  if (!popup) {
    reject(new Error('Please allow popups to continue with social sign-in.'));
    return;
  }

  const cleanup = () => {
    window.removeEventListener('message', handleMessage);
    clearInterval(watchdog);
    clearTimeout(timeoutId);
  };

  const watchdog = setInterval(() => {
    if (popup.closed) {
      cleanup();
      reject(new Error('Sign-in window closed before completion.'));
    }
  }, 500);

  const timeoutId = setTimeout(() => {
    cleanup();
    try {
      popup.close();
    } catch {
      /* noop */
    }
    reject(new Error('Timed out while waiting for the authentication provider. Please try again.'));
  }, POPUP_TIMEOUT_MS);

  const handleMessage = (event) => {
    if (!isExpectedOAuthMessageOrigin(event.origin)) {
      if (process.env.NODE_ENV !== 'production') {
        const expected = Array.from(OAUTH_ALLOWED_ORIGINS).join(', ');
        console.warn(`[OAuth] Ignoring message from unexpected origin: ${event.origin}. Expected one of: ${expected}`);
      }
      return;
    }

    const payload = event.data;
    if (!payload || payload.provider !== provider) {
      return;
    }

    if (event.source && typeof event.source.postMessage === 'function') {
      try {
        event.source.postMessage({ type: 'vm-oauth-ack', provider }, event.origin);
      } catch {
        // Best effort only.
      }
    }

    cleanup();
    try {
      popup.close();
    } catch {
      /* noop */
    }
    resolve(payload);
  };

  window.addEventListener('message', handleMessage);
});

export const startOAuthLogin = async (provider, options = {}) => {
  const url = buildOAuthLoginUrl(provider, options);
  const result = await openOAuthPopup(provider, url);

  if (result.status === 'authenticated') {
    const sessionPayload = await resolveOAuthSession(result.data);
    return {
      ...result,
      data: sessionPayload
    };
  }

  return result;
};

export const startOAuthLink = async (provider, options = {}) => {
  const response = await authAPI.startLinkSession(provider, {
    origin: window.location.origin,
    redirect: options.redirect || window.location.origin
  });

  const authUrl = response.data?.authUrl;
  if (!authUrl) {
    throw new Error('Unable to start link session for the selected provider.');
  }

  const result = await openOAuthPopup(provider, authUrl);

  if (result.status === 'linked' && (result.data?.token || result.data?.sessionCode)) {
    const sessionPayload = await resolveOAuthSession(result.data);
    return {
      ...result,
      data: sessionPayload
    };
  }

  return result;
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token) => {
  const response = await authAPI.verifyEmail(token);
  return response.data;
};

/**
 * Request a password reset link
 */
export const requestPasswordReset = async (email) => {
  const response = await authAPI.requestPasswordReset(email);
  return response.data;
};

/**
 * Reset password using a token
 */
export const resetPassword = async (token, password) => {
  const response = await authAPI.resetPassword(token, password);
  return response.data;
};

/**
 * Log out the current user
 */
export const logout = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const fetchLinkedProviders = async () => {
  const response = await authAPI.getLinkedProviders();
  return response.data;
};

export const unlinkOAuthProvider = async (provider) => {
  const response = await authAPI.unlinkProvider(provider);
  const currentUser = getCurrentUser();
  if (currentUser) {
    persistUser({
      ...currentUser,
      authProviders: response.data.providers,
      primaryAuthProvider: response.data.primaryAuthProvider
    });
  }
  return response.data;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => !!localStorage.getItem(TOKEN_KEY);

/**
 * Get current user from localStorage
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get the auth token
 */
export const getToken = () => localStorage.getItem(TOKEN_KEY);
